import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Get the appropriate color for a status
 * @param status The status value
 * @returns A CSS color string
 */
export function getStatusColor(status: string): string {
  //console.log(status);
  
  switch (status) {
    // Calendar event statuses
    case 'pending':
      return '#FFA500'; // Orange
    case 'confirmed':
      return '#4CAF50'; // Green
    case 'completed':
      return '#2196F3'; // Blue
    case 'cancelled':
      return '#F44336'; // Red
    // Invoice statuses (keeping these for backward compatibility)
    case 'draft':
      return '#9CA3AF';
    case 'awaiting_payment':
      return '#F59E0B';
    case 'paid':
      return '#10B981';
    case 'overdue':
      return '#EF4444';
    case 'void':
      return '#6B7280';
    case 'awaiting_approval':
      return '#8B5CF6';
    case 'approved':
      return '#3B82F6';
    case 'partially_paid':
      return '#14B8A6';
    case 'unpaid':
      return '#DC2626';
    default:
      return '#6B7280'; // Default gray color
  }
}

/**
 * Get the appropriate badge variant for a status
 * @param status The status value
 * @returns A badge variant string
 */
export function getStatusBadgeVariant(status: string): "default" | "secondary" | "destructive" | "outline" | "warning" | "success" {
  switch (status.toLowerCase()) {
    case 'pending':
      return 'secondary';
    case 'scheduled':
      return 'warning';
    case 'in_progress':
      return 'warning';
    case 'completed':
      return 'success';
    case 'cancelled':
      return 'destructive';
    case 'confirmed':
      return 'success';
    case 'draft':
      return 'secondary';
    case 'awaiting_payment':
      return 'warning';
    case 'paid':
      return 'success';
    case 'overdue':
      return 'destructive';
    case 'void':
      return 'outline';
    case 'awaiting_approval':
      return 'warning';
    case 'approved':
      return 'success';
    case 'partially_paid':
      return 'warning';
    case 'unpaid':
      return 'destructive';
    default:
      return 'secondary';
  }
}

/**
 * Format a number as a currency amount
 * @param amount The amount to format
 * @param currencyCode The currency code (default: USD)
 * @returns The formatted currency string
 */
export function formatCurrency(amount: number, currencyCode = 'USD'): string {
  // Ensure we have a valid number to format
  const validAmount = typeof amount === 'number' && !isNaN(amount) ? amount : 0;
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2 // Limit to exactly 2 decimal places
  }).format(validAmount);
}

/**
 * Format a duration in minutes to a readable format (e.g., 1h 30m)
 * @param minutes The total minutes
 * @param showSeconds Show seconds for active timers
 * @returns The formatted duration string
 */
export function formatDuration(minutes: number, showSeconds = false): string {
  // Ensure we have a valid number
  const validMinutes = typeof minutes === 'number' && !isNaN(minutes) ? minutes : 0;
  
  if (validMinutes === 0) {
    return showSeconds ? '0:00' : '0m';
  }
  
  if (showSeconds) {
    // Format as MM:SS for active timers (minutes parameter is actually seconds in this case)
    const mins = Math.floor(validMinutes / 60);
    const secs = Math.floor(validMinutes % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  } else {
    // Format as Xh Ym for completed time entries
    const hours = Math.floor(validMinutes / 60);
    const mins = Math.floor(validMinutes % 60);
    
    if (hours === 0) {
      return `${mins}m`;
    } else if (mins === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${mins}m`;
    }
  }
}

/**
 * Determine if a color is light or dark to ensure proper text contrast
 * @param hexColor The hex color to check (e.g., #FFFFFF)
 * @returns True if the color is light (should use dark text), false otherwise
 */
export function isColorLight(hexColor: string): boolean {
  // Remove # if present
  const hex = hexColor.replace('#', '');
  
  // Convert hex to RGB
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Calculate luminance (perceived brightness)
  // Using the formula: 0.299*R + 0.587*G + 0.114*B
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // If luminance is greater than 0.5, the color is considered light
  return luminance > 0.5;
}

/**
 * Format a timestamp into a human-readable date and time string
 * @param timestamp The timestamp to format (Date object or ISO string)
 * @param format The desired format - 'short' for mm/dd/yy time or 'long' for MMM DD, YYYY time (default: 'long')
 * @returns The formatted date and time string
 */
export function formatTimestamp(timestamp: Date | string, format: 'short' | 'long' = 'long'): string {
  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  
  const options: Intl.DateTimeFormatOptions = format === 'short' ? {
    year: '2-digit',
    month: '2-digit', 
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  } : {
    year: 'numeric',
    month: 'short',
    day: 'numeric', 
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  };

  return new Intl.DateTimeFormat('en-US', options).format(date);
}

/**
 * Convert a human-friendly label into a service type value.
 * Lowercase, replace spaces with underscores, trim.
 */
export function toServiceTypeValue(label: string): string {
  if (!label) return '';
  return label
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
}

/**
 * Convert a human-friendly label into a service category value.
 * Lowercase, replace spaces with underscores, trim.
 */
export function toServiceCategoryValue(label: string): string {
  if (!label) return '';
  return label
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
}

/**
 * Convert a human-friendly label into a service status value.
 * Lowercase, replace spaces with underscores, trim.
 */
export function toServiceStatusValue(label: string): string {
  if (!label) return '';
  return label
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
}

// Audit writes are server-owned via RTDB subscribers. Client should not write audits.
